import { Injectable } from "@angular/core";

export type ReportMeasurementResult = { result: { length: string; area: string; objectDistance?: string }; mode: string; unit: string; id: string };

/** A stored measurement item's `data`, as the legacy measurement tab and the Merry tab (MerryMeasurementItems) write it. */
type MeasurementItemData = { id?: string; mode?: string; unit?: string; result?: { length?: unknown; area?: unknown; objectDistance?: unknown } };

/** The modes whose value is an area: the legacy tab's area / missingCorner, the Merry tab's tipDefect. */
const AREA_MODES = ["area", "missingcorner", "tipdefect"];
/** Shown when an item holds no value (a measurement that was never completed). */
const NO_VALUE = "-";

const MAX_OBJECT_DISTANCE = 10000;
const MAX_MM = 100000;
const MAX_INCH = 10000;
const MAX_AREA_MM = 100000000;
const MAX_AREA_INCH = 1000000;
const INCH_SCALE = 25.4;
const SQUARE_INCH_SCALE = INCH_SCALE * INCH_SCALE;

@Injectable({ providedIn: "root" })
export class LegacyReportMeasurementService {
  /**
   * One result per measurement id, converted to the unit stored on the item and rounded. The legacy tab stores one item
   * per drawn object (points, lines, the polygon) and only some of them carry the result, so the first item holding a
   * value wins over the first item of the id.
   */
  public toReportMeasurementResult(measurement: unknown): ReportMeasurementResult[] {
    if (!Array.isArray(measurement)) {
      return [];
    }

    const dataById = new Map<string, MeasurementItemData>();
    measurement.forEach((item) => {
      const data: MeasurementItemData | undefined = item?.data;
      if (item?.type === "image" || !data?.id) {
        return;
      }
      const kept = dataById.get(data.id);
      if (!kept || (!this.hasValue(kept) && this.hasValue(data))) {
        dataById.set(data.id, data);
      }
    });

    return [...dataById.values()].map((data) => this.convertData(data));
  }

  public isAreaMode(mode: string | undefined): boolean {
    return AREA_MODES.includes(mode?.toLowerCase() ?? "");
  }

  private hasValue(data: MeasurementItemData): boolean {
    const value = this.isAreaMode(data.mode) ? data.result?.area : data.result?.length;
    return this.toNumber(value) !== null;
  }

  private convertData(data: MeasurementItemData): ReportMeasurementResult {
    const isInch = data.unit?.toLowerCase() === "inch";
    const length = this.convert(this.toNumber(data.result?.length), isInch ? INCH_SCALE : 1);
    const area = this.convert(this.toNumber(data.result?.area), isInch ? SQUARE_INCH_SCALE : 1);
    const objectDistance = this.convert(this.toNumber(data.result?.objectDistance), isInch ? INCH_SCALE : 1);

    return {
      id: data.id,
      mode: data.mode ?? "",
      unit: data.unit ?? "mm",
      result: {
        length: this.formatLength(length, isInch),
        area: this.formatArea(area, isInch),
        objectDistance: this.formatObjectDistance(objectDistance, isInch)
      }
    };
  }

  private formatLength(length: number | null, isInch: boolean): string {
    if (length === null) {
      return NO_VALUE;
    }
    return length >= (isInch ? MAX_INCH : MAX_MM) ? "OVER" : this.round(length, isInch);
  }

  private formatArea(area: number | null, isInch: boolean): string {
    if (area === null) {
      return NO_VALUE;
    }
    if (area < 0) {
      return "WRONG FIGURE";
    }
    return area >= (isInch ? MAX_AREA_INCH : MAX_AREA_MM) ? "OVER" : this.round(area, isInch);
  }

  /** Undefined when there is none: the report only prints the object distance of the items that have one. */
  private formatObjectDistance(distance: number | null, isInch: boolean): string | undefined {
    if (distance === null) {
      return undefined;
    }
    return distance >= MAX_OBJECT_DISTANCE ? "-----" : this.round(distance, isInch);
  }

  /** Three significant digits shown with two decimals in millimetres, four and three in inches. */
  private round(value: number, isInch: boolean): string {
    return isInch ? Number(value.toPrecision(4)).toFixed(3) : Number(value.toPrecision(3)).toFixed(2);
  }

  private convert(value: number | null, scale: number): number | null {
    return value === null ? null : value / scale;
  }

  /** Stored values are numbers or numeric strings; anything else (null, "", text) is no value. */
  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }
}
